// Configuration des flux partenaires JSON dynamiques
// Chargement direct (CORS) avec cache localStorage et fallback

import type { PartnerBook } from './partnerBooks';

// Configuration d'un flux partenaire
interface PartnerFeed {
  id: string;
  name: string;
  feedUrl: string;
  cacheDuration: number; // minutes
}

// Version du cache — à incrémenter quand la structure des données change
const CACHE_VERSION = 3;

// Structure du cache localStorage
interface CachedFeed {
  version: number;
  timestamp: number;
  expiresAt: number;
  books: PartnerBook[];
  source: string;
}

// Liste des partenaires (extensible)
const PARTNER_FEEDS: PartnerFeed[] = [
  {
    id: 'iliade',
    name: 'Institut Iliade',
    feedUrl: 'https://boutique.institut-iliade.com/wp-json/polemia-ai/v1/products',
    cacheDuration: 60
  }
  // Autres partenaires ajoutables ici :
  // {
  //   id: 'nouvelle-librairie',
  //   name: 'La Nouvelle Librairie',
  //   feedUrl: 'https://nouvelle-librairie.com/api/products.json',
  //   cacheDuration: 60
  // }
];

// Génère la clé localStorage pour un partenaire
function getCacheKey(feedId: string): string {
  return `polemia_partner_${feedId}_cache`;
}

// Récupère le cache s'il est encore valide
function getCachedFeed(feedId: string): PartnerBook[] | null {
  try {
    const key = getCacheKey(feedId);
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    
    const data: CachedFeed = JSON.parse(cached);
    if (data.version === CACHE_VERSION && Date.now() < data.expiresAt) {
      return data.books;
    }
    return null; // Expiré, mais conservé pour fallback
  } catch {
    return null;
  }
}

// Sauvegarde les données en cache
function saveFeedToCache(feedId: string, books: PartnerBook[], durationMinutes: number): void {
  try {
    const key = getCacheKey(feedId);
    const data: CachedFeed = {
      version: CACHE_VERSION,
      timestamp: Date.now(),
      expiresAt: Date.now() + durationMinutes * 60 * 1000,
      books,
      source: feedId
    };
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn(`[PartnerFeeds] Failed to cache ${feedId}:`, error);
  }
}

// Récupère le cache expiré comme fallback
function getExpiredCache(feedId: string): PartnerBook[] | null {
  try {
    const key = getCacheKey(feedId);
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    const data: CachedFeed = JSON.parse(cached);
    if (data.version !== CACHE_VERSION) return null;
    return data.books;
  } catch {
    return null;
  }
}

// Décode les entités HTML (&nbsp; etc.)
function decodeHtmlEntities(str: string): string {
  return str
    .replace(/&amp;nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

// Transforme les données brutes du flux vers le format PartnerBook
function transformFeedData(data: unknown): PartnerBook[] {
  const rawData = data as Record<string, unknown>;
  const products = (rawData.products || rawData.books || []) as Record<string, unknown>[];
  
  return products
    .filter((p): p is Record<string, unknown> => 
      typeof p === 'object' && 
      p !== null && 
      typeof p.title === 'string' && 
      typeof p.price === 'string' && 
      typeof p.url === 'string'
    )
    .map((p) => ({
      title: decodeHtmlEntities(p.title as string),
      author: typeof p.author === 'string' ? decodeHtmlEntities(p.author) : undefined,
      pages: typeof p.pages === 'number' ? p.pages : undefined,
      price: p.price as string,
      url: p.url as string,
      themes: Array.isArray(p.themes) ? (p.themes as string[]) : [],
      authors: Array.isArray(p.authors) ? (p.authors as string[]) : [],
      concepts: Array.isArray(p.concepts) ? (p.concepts as string[]) : []
    }));
}

// Charge un flux partenaire individuel
async function loadPartnerFeed(feed: PartnerFeed): Promise<PartnerBook[]> {
  // 1. Vérifier le cache valide
  const cached = getCachedFeed(feed.id);
  if (cached) {
    console.log(`[PartnerFeeds] ${feed.id}: using cached data (${cached.length} books)`);
    return cached;
  }
  
  // 2. Tenter le fetch direct
  try {
    const response = await fetch(feed.feedUrl, {
      headers: { 'Accept': 'application/json' },
      // Timeout implicite via AbortController si besoin
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    const books = transformFeedData(data);
    
    if (books.length > 0) {
      // Sauvegarder en cache
      saveFeedToCache(feed.id, books, feed.cacheDuration);
      console.log(`[PartnerFeeds] ${feed.id}: fetched ${books.length} books`);
    }
    
    return books;
  } catch (error) {
    console.warn(`[PartnerFeeds] ${feed.id}: fetch failed`, error);
    
    // 3. Fallback vers cache expiré
    const expired = getExpiredCache(feed.id);
    if (expired) {
      console.log(`[PartnerFeeds] ${feed.id}: using expired cache as fallback (${expired.length} books)`);
      return expired;
    }
    
    // Aucun cache disponible
    return [];
  }
}

// Charge tous les flux partenaires en parallèle
export async function loadAllPartnerFeeds(): Promise<PartnerBook[]> {
  const results = await Promise.all(
    PARTNER_FEEDS.map(feed => loadPartnerFeed(feed))
  );
  return results.flat();
}

// Utilitaire de debug : vide le cache de tous les partenaires
export function clearPartnerCache(): void {
  PARTNER_FEEDS.forEach(feed => {
    localStorage.removeItem(getCacheKey(feed.id));
  });
  console.log('[PartnerFeeds] Cache cleared');
}

// Export de la config pour debug
export function getPartnerFeedsConfig(): PartnerFeed[] {
  return [...PARTNER_FEEDS];
}
